import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableIconButton } from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  CollapseAllIcon,
  DateInput,
  ExpandAllIcon,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { ExpenseCategory } from '../../types/shipment';
import { ShipmentExpenseCategoriesReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportShipmentExpenseCategories } from './exportShipmentReports';

export function ShipmentExpenseCategoriesPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [report, setReport] = useState<ShipmentExpenseCategoriesReport | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('categoryName');

  const rows = useMemo(
    () =>
      report
        ? sortItems(report.data, sort, {
            categoryName: (r) => r.categoryName,
            gross: (r) => r.totals.gross,
          })
        : [],
    [report, sort],
  );

  useEffect(() => {
    void Promise.all([
      api.getCustomerLookup({ active: 'true' }),
      api.getExpenseCategories(false),
    ]).then(([c, cats]) => {
      setCustomers(c);
      setCategories(cats);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getShipmentExpenseCategoriesReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, customerId, categoryId]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            <TableIconButton
              label={labels.showInvoices}
              onClick={() => {
                const next: Record<string, boolean> = {};
                for (const row of rows) next[String(row.categoryId)] = true;
                setExpanded(next);
              }}
            >
              <ExpandAllIcon className="h-4 w-4" />
            </TableIconButton>
            <TableIconButton
              label={labels.hideInvoices}
              onClick={() => setExpanded({})}
            >
              <CollapseAllIcon className="h-4 w-4" />
            </TableIconButton>
            <ReportExportButtons
              disabled={!report || loading}
              onExcel={() => {
                if (!report) return;
                exportShipmentExpenseCategories({ report, format: 'excel' });
              }}
              onPdf={() => {
                if (!report) return;
                exportShipmentExpenseCategories({ report, format: 'pdf' });
              }}
            />
          </>
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().nav.expenseCategories}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.allCategories}
            options={categories.map((c) => ({
              value: String(c.id),
              label: c.name,
            }))}
            onChange={(e) => setCategoryId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>
      {error ? <Callout variant="danger">{error}</Callout> : null}
      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || rows.length === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <th className="w-10 px-2 py-3" />
                <SortableTh sortKey="categoryName" sort={sort} onSort={toggleSort}>
                  {t().nav.expenseCategories}
                </SortableTh>
                <th className="px-4 py-3 text-right font-semibold">
                  {labels.expenseGross}
                </th>
                <th className="px-4 py-3 text-right font-semibold">
                  {labels.billable}
                </th>
                <th className="px-4 py-3 text-right font-semibold">
                  {labels.internal}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const key = String(row.categoryId);
                const open = Boolean(expanded[key]);
                return (
                  <Fragment key={key}>
                    <tr className="border-b border-line/70">
                      <td className="px-2 py-3">
                        <TableIconButton
                          label={open ? labels.hideInvoices : labels.showInvoices}
                          onClick={() =>
                            setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
                          }
                        >
                          {open ? (
                            <CollapseAllIcon className="h-4 w-4" />
                          ) : (
                            <ExpandAllIcon className="h-4 w-4" />
                          )}
                        </TableIconButton>
                      </td>
                      <td className="px-4 py-3 font-medium">{row.categoryName}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(row.totals.gross)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(row.billable.gross)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(row.internal.gross)}
                      </td>
                    </tr>
                    {open
                      ? row.expenses.map((exp) => (
                          <tr
                            key={exp.id}
                            className="border-b border-line/40 bg-surface/40"
                          >
                            <td />
                            <td className="px-4 py-2 text-sm text-muted" colSpan={2}>
                              <Link
                                to={`/shipment-files/${exp.shipmentFileId}`}
                                className="text-teal-700 hover:underline dark:text-teal-300"
                              >
                                {exp.fileNo}
                              </Link>
                              {' · '}
                              {exp.customer.name}
                              {exp.description ? ` · ${exp.description}` : ''}
                            </td>
                            <td className="px-4 py-2 text-right text-sm tabular-nums">
                              {formatMoney(exp.amount.gross)}
                            </td>
                            <td className="px-4 py-2 text-sm text-muted">
                              {exp.billable ? labels.billable : labels.internal}
                            </td>
                          </tr>
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </DataTablePanel>
      )}
    </PageShell>
  );
}
